/**
 * Helpers to translate Material UI DataGrid query options
 * into Django REST Framework compatible query params.
 *
 * Supports:
 * - Pagination (page, pageSize)
 * - Ordering (?ordering=field,-field)
 * - Search (?search=)
 * - django-filter style filters
 *
 * WARNING:
 * Some filter operators used here (e.g. __equals, __notequal, __notcontains)
 * are NOT part of django-filter defaults.
 *
 * TODO:
 * Review and align filter operators after django-filter,
 * SearchFilter and OrderingFilter are fully configured in the backend.
 */

// -----------------------------------------------------------------------------
// Filter operators mapping (Material UI DataGrid -> DRF)
// -----------------------------------------------------------------------------

const FILTER_OPERATOR_MAP = {
    // Comparison operators
    '=': (field, value) => ({ [field]: value }),
    '!=': (field, value) => ({ [`${field}__ne`]: value }),
    '>': (field, value) => ({ [`${field}__gt`]: value }),
    '>=': (field, value) => ({ [`${field}__gte`]: value }),
    '<': (field, value) => ({ [`${field}__lt`]: value }),
    '<=': (field, value) => ({ [`${field}__lte`]: value }),

    // Text operators
    // equals: (field, value) => ({ [`${field}__equals`]: value }),
    equals: (field, value) => ({ [field]: value }),
    doesNotEqual: (field, value) => ({ [`${field}__notequal`]: value }),
    contains: (field, value) => ({ [`${field}__contains`]: value }),
    icontains: (field, value) => ({ [`${field}__icontains`]: value }),
    doesNotContain: (field, value) => ({ [`${field}__notcontains`]: value }),
    startsWith: (field, value) => ({ [`${field}__startswith`]: value }),
    endsWith: (field, value) => ({ [`${field}__endswith`]: value }),

    // List / set operators
    isAnyOf: (field, value) => ({
        [`${field}__in`]: Array.isArray(value) ? value.join(',') : value,
    }),

    // Boolean operators
    is: (field, value) => ({ [`${field}__is`]: value }),

    // Null operators
    isEmpty: (field) => ({ [`${field}__isnull`]: true }),
    isNotEmpty: (field) => ({ [`${field}__isnull`]: false }),
}

// -----------------------------------------------------------------------------
// Filter parsing
// -----------------------------------------------------------------------------

export const parseFilterClause = (clause) => {
    const { field, operator, value } = clause

    const handler = FILTER_OPERATOR_MAP[operator]
    if (!handler || !field) {
        return {}
    }

    return handler(field, value)
}

const parseFilterModel = (filterModel) => {
    if (!filterModel || !Array.isArray(filterModel.items)) {
        return {}
    }

    let filters = {}

    filterModel.items.forEach((item) => {
        if (!item.field || !item.operator) {
            return
        }

        // Operators that do not require explicit value
        if (['isEmpty', 'isNotEmpty'].includes(item.operator)) {
            filters = {
                ...filters,
                ...parseFilterClause(item),
            }
            return
        }

        // Avoid sending incomplete filters while user is editing
        if (
            item.value === undefined ||
            item.value === null ||
            item.value === ''
        ) {
            return
        }

        filters = {
            ...filters,
            ...parseFilterClause(item),
        }
    })

    return filters
}

// -----------------------------------------------------------------------------
// Query options parsing (pagination, ordering, search, filters)
// -----------------------------------------------------------------------------

/**
 * Translates Material UI DataGrid server-side state into
 * Django REST Framework compatible query parameters.
 *
 * This helper acts as a translation layer between the UI and the API,
 * converting pagination, sorting, search and filtering options from
 * the DataGrid format into DRF query params.
 *
 * Supported features:
 * - Pagination:
 *   - Converts 0-based DataGrid pages into 1-based DRF pages
 *   - Maps `paginationModel.page` -> `page`
 *   - Maps `paginationModel.pageSize` -> `pageSize`
 *
 * - Ordering:
 *   - Converts DataGrid `sortModel` into DRF `ordering` param
 *   - Example: [{ field: 'created_at', sort: 'desc' }] -> ?ordering=-created_at
 *
 * - Search:
 *   - Maps a free-text `search` value to DRF SearchFilter (`?search=`)
 *   - Empty or whitespace-only values are ignored
 *
 * - Filtering:
 *   - Converts DataGrid `filterModel` into django-filter style query params
 *   - Example:
 *       { field: 'is_active', operator: '=', value: true }
 *       -> ?is_active=true
 *
 * IMPORTANT:
 * Some filter operators implemented here (e.g. `__equals`, `__notequal`,
 * `__notcontains`) are NOT part of django-filter defaults.
 *
 * TODO:
 * Review and align filter operators once django-filter, SearchFilter
 * and OrderingFilter are fully configured in the backend.
 *
 * @param {Object} queryOptions - DataGrid server-side query options.
 *
 * @param {Object} [queryOptions.paginationModel]
 * @param {number} queryOptions.paginationModel.page
 *   Zero-based page index from Material UI DataGrid.
 * @param {number} queryOptions.paginationModel.pageSize
 *   Number of items per page.
 *
 * @param {Array<Object>} [queryOptions.sortModel]
 * @param {string} queryOptions.sortModel[].field
 *   Field name used for ordering.
 * @param {'asc'|'desc'} queryOptions.sortModel[].sort
 *   Sort direction.
 *
 * @param {string} [queryOptions.search]
 *   Free-text search value mapped to DRF SearchFilter.
 *
 * @param {Object} [queryOptions.filterModel]
 * @param {Array<Object>} queryOptions.filterModel.items
 *   List of filter clauses coming from DataGrid.
 *
 * @returns {Object} Axios-compatible config object containing `params`.
 *   Example:
 *     {
 *       params: {
 *         page: 1,
 *         pageSize: 10,
 *         ordering: '-created_at',
 *         search: 'example',
 *         is_active: true
 *       }
 *     }
 */
export const parseQueryOptions = (queryOptions = {}) => {
    const { paginationModel, sortModel, search, filterModel } = queryOptions

    let params = {}

    // Pagination
    // Material UI DataGrid is 0-based, DRF expects 1-based pages
    if (paginationModel) {
        params.page = paginationModel.page + 1
        params.pageSize = paginationModel.pageSize
    }

    // Ordering (DRF OrderingFilter)
    if (Array.isArray(sortModel) && sortModel.length > 0) {
        params.ordering = sortModel
            .map((item) => (item.sort === 'desc' ? `-${item.field}` : item.field))
            .join(',')
    }

    // Search (DRF SearchFilter)
    if (typeof search === 'string' && search.trim() !== '') {
        params.search = search.trim()
    }

    // Filters
    const filters = parseFilterModel(filterModel)

    return {
        params: {
            ...params,
            ...filters,
        },
    }
}

// -----------------------------------------------------------------------------
//  Exemplo
// import axiosInstance from './axiosInstance'
// import { parseQueryOptions } from './queryHelpers'

// // Businesses
// export const customerService = {
//   getBusinesses: (queryOptions) =>
//     axiosInstance.get(
//       `${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/`,
//       parseQueryOptions(queryOptions)
//     ),
// }
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Parametros:
//
// const queryOptions = {
//     paginationModel: {
//         page: 0,        // DataGrid is 0-based
//         pageSize: 10,
//     },

//     sortModel: [
//         {
//             field: 'created_at',
//             sort: 'desc', // 'asc' | 'desc'
//         },
//     ],

//     search: 'chevrolet',

//     filterModel: {
//         items: [
//             {
//                 field: 'is_active',
//                 operator: '=',
//                 value: true,
//             },
//             {
//                 field: 'display_name',
//                 operator: 'contains',
//                 value: 'SJC',
//             },
//             {
//                 field: 'provider_uuid',
//                 operator: 'isAnyOf',
//                 value: [
//                     'a3d2e1f4-1111-2222-3333-444455556666',
//                     'b4c3d2e1-7777-8888-9999-000011112222',
//                 ],
//             },
//             {
//                 field: 'deleted_at',
//                 operator: 'isEmpty',
//             },
//         ],
//     },
// }
// -----------------------------------------------------------------------------
